import { array, number, object, string } from "yup";

import { PaymentType, PaymentTypeCreateInput } from "@/interfaces/payment-type.interface";
import { PaymentTypeMessages } from "@/messages/platforms/talk2PayHome/payment-type.messages";

export type PaymentTypeFormData = PaymentTypeCreateInput & {
  interestRates: number[];
};

export const CREDIT_CARD_PAYMENT_TYPE_NAME = "Cartão";

export const parseMonthlyInterestRates = (json?: string | null, maxCount = 1): number[] => {
  const count = Math.max(1, maxCount);
  const rates = Array.from({ length: count }, () => 0);

  if (!json) {
    return rates;
  }

  try {
    const parsed = JSON.parse(json) as Record<string, number>;
    for (let i = 1; i <= count; i += 1) {
      const value = parsed[String(i)];
      rates[i - 1] = Number.isFinite(Number(value)) ? Number(value) : 0;
    }
  } catch {
    return rates;
  }

  return rates;
};

export const serializeMonthlyInterestRates = (rates: number[], maxCount: number): string => {
  const payload: Record<string, number> = {};
  const count = Math.max(1, maxCount);
  for (let i = 1; i <= count; i += 1) {
    payload[String(i)] = Number(rates[i - 1]) || 0;
  }
  return JSON.stringify(payload);
};

export const buildPaymentTypeInitialData = (data?: PaymentType | null): PaymentTypeFormData => {
  const maxInstallmentCount = data?.maxInstallmentCount ?? 1;
  return {
    paymentTypesID: data?.paymentTypesID ?? 0,
    active: true,
    discountApplied: data?.discountApplied ?? 0,
    discountOver: data?.discountOver ?? 0,
    maxInstallmentCount,
    minInstallmentAmount: data?.minInstallmentAmount ?? 0,
    monthlyInterestRates: data?.monthlyInterestRates ?? "{}",
    interestRates: parseMonthlyInterestRates(data?.monthlyInterestRates, maxInstallmentCount),
  };
};

export const buildPaymentTypeSchema = (messages: PaymentTypeMessages, isCreditCard: boolean) =>
  object().shape({
    paymentTypesID: number()
      .typeError(messages.paymentTypeRequired)
      .min(1, messages.paymentTypeRequired)
      .required(messages.paymentTypeRequired),
    discountApplied: number()
      .typeError(messages.discountAppliedRequired)
      .min(0, messages.positiveNumber)
      .required(messages.discountAppliedRequired),
    discountOver: number()
      .typeError(messages.discountOverRequired)
      .min(0, messages.positiveNumber)
      .required(messages.discountOverRequired),
    maxInstallmentCount: number()
      .typeError(messages.maxInstallmentCountRequired)
      .min(1, messages.maxInstallmentCountMin)
      .when([], {
        is: () => isCreditCard,
        then: (schema) => schema.required(messages.maxInstallmentCountRequired),
        otherwise: (schema) => schema.notRequired(),
      }),
    minInstallmentAmount: number()
      .typeError(messages.minInstallmentAmountRequired)
      .min(0, messages.positiveNumber)
      .when([], {
        is: () => isCreditCard,
        then: (schema) => schema.required(messages.minInstallmentAmountRequired),
        otherwise: (schema) => schema.notRequired(),
      }),
    interestRates: array()
      .of(number().typeError(messages.positiveNumber).min(0, messages.positiveNumber))
      .when([], {
        is: () => isCreditCard,
        then: (schema) =>
          schema.test("rates-length", messages.interestRatesRequired, function (value) {
            const max = Number(this.parent.maxInstallmentCount) || 1;
            return Array.isArray(value) && value.length >= max;
          }),
        otherwise: (schema) => schema.notRequired(),
      }),
    monthlyInterestRates: string().notRequired(),
  });
